// Track 2 — Subscription page.
//
// Shows the rider's current plan + status, the catalog of plans they
// can switch to, and a placeholder billing history block. Checkout +
// portal flows go through the API which redirects to Stripe (or to a
// stub-success page when STRIPE_SECRET_KEY isn't set yet).

import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Stack,
  CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from './services/useCurrentUser';
import {
  useSubscription,
  PlanRow,
  refreshSubscription,
} from './services/useSubscription';
import PageLayout from './components/PageLayout';
import { colors } from './theme';

const API = process.env.REACT_APP_API || 'https://api.aero.chat';
const TOKEN_KEY = process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token';
// Wave 16 — paid tiers are visible but not purchasable until Stripe goes
// live. Flip REACT_APP_STRIPE_ENABLED=true at the build step to re-enable.
const STRIPE_ENABLED = process.env.REACT_APP_STRIPE_ENABLED === 'true';

export default function Subscription() {
  const { t } = useTranslation('subscription');
  const navigate = useNavigate();
  const user = useCurrentUser();
  const { plans, subscription, isLoading, error, refresh } = useSubscription();
  const [busyPlanId, setBusyPlanId] = useState<string | null>(null);

  useEffect(() => {
    if (!user.isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [user.isAuthenticated, navigate]);

  const currentPlanId = subscription?.planId ?? 'free';
  const currentPlan = plans.find((p) => p.id === currentPlanId);

  const startCheckout = async (planId: string) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      navigate('/login');
      return;
    }
    setBusyPlanId(planId);
    try {
      const r = await fetch(`${API}/subscriptions/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ planId }),
      });
      const body = await r.json();
      if (body?.url) {
        // External URL → navigate via window.location so the browser
        // follows the redirect. In-app stub success path also works.
        if (body.url.startsWith('http')) {
          window.location.href = body.url;
        } else {
          navigate(body.url);
        }
      }
    } catch {
      // no-op; UI shows error via refresh()
    } finally {
      setBusyPlanId(null);
      await refresh();
    }
  };

  const openPortal = async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      navigate('/login');
      return;
    }
    const r = await fetch(`${API}/subscriptions/portal`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    const body = await r.json();
    if (body?.url) {
      if (body.url.startsWith('http')) {
        window.location.href = body.url;
      } else {
        navigate(body.url);
      }
    }
  };

  if (!user.isAuthenticated) return null;

  // Rapha members reach full access via their membership, not via a
  // subscription tier. The plan catalog doesn't apply to them — show a
  // friendly explanation card instead so a direct URL hit isn't
  // confusing. The Subscription menu item is hidden for these users
  // upstream (AppHeader), so this path is only hit via typed URLs /
  // stale bookmarks.
  if (user.role === 'rapha') {
    return (
      <PageLayout fullBleed>
      <Box
        sx={{
          minHeight: '100%',
          backgroundColor: colors.bg,
          background: colors.gradHero,
          py: { xs: 4, md: 8 },
        }}
      >
        <Box sx={{ maxWidth: 640, mx: 'auto', px: { xs: 2.5, md: 4 } }}>
          <Typography
            sx={{
              fontSize: '0.6875rem',
              fontWeight: 600,
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: colors.brand,
              mb: 1.25,
            }}
          >
            aero.chat × Rapha
          </Typography>
          <Typography sx={{ fontSize: { xs: '2rem', md: '2.5rem' }, fontWeight: 700, color: colors.text, mb: 1 }}>
            {t('rapha.title')}
          </Typography>
          <Box
            sx={{
              mt: 4,
              p: 4,
              borderRadius: 3,
              backgroundColor: colors.surface,
              border: `1px solid ${colors.borderFocus}`,
            }}
          >
            <Typography sx={{ color: colors.text, fontSize: '1rem', lineHeight: 1.6 }}>
              {t('rapha.body')}
            </Typography>
            <Button
              onClick={() => navigate('/session')}
              variant="contained"
              sx={{
                mt: 3,
                backgroundColor: colors.brand,
                color: colors.bg,
                fontWeight: 700,
                letterSpacing: '0.05em',
                '&:hover': { backgroundColor: colors.brand },
              }}
            >
              {t('rapha.back')}
            </Button>
          </Box>
        </Box>
      </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout fullBleed>
    <Box
      sx={{
        minHeight: '100%',
        backgroundColor: colors.bg,
        background: colors.gradHero,
        py: { xs: 4, md: 8 },
      }}
    >
      <Box sx={{ maxWidth: 920, mx: 'auto', px: { xs: 2.5, md: 4 } }}>
        <Typography
          sx={{
            fontSize: '0.6875rem',
            fontWeight: 600,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: colors.brand,
            mb: 1.25,
          }}
        >
          aero.chat
        </Typography>
        <Typography sx={{ fontSize: { xs: '2rem', md: '2.5rem' }, fontWeight: 700, color: colors.text, mb: 1 }}>
          {t('title')}
        </Typography>
        <Typography sx={{ fontSize: '1rem', color: colors.textMuted, mb: 5 }}>
          {t('subtitle')}
        </Typography>

        {isLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: colors.textMuted }}>
            <CircularProgress size={18} />
            <Typography>{t('loading')}</Typography>
          </Box>
        )}

        {error && (
          <Box sx={{ mb: 3, p: 2, borderRadius: 2, border: `1px solid ${colors.danger}`, backgroundColor: 'rgba(255,71,87,0.06)' }}>
            <Typography sx={{ color: colors.danger }}>{t('errorLoading')}</Typography>
          </Box>
        )}

        {!isLoading && subscription && currentPlan && (
          <CurrentPlanCard
            plan={currentPlan}
            status={subscription.status}
            cancelAtPeriodEnd={subscription.cancelAtPeriodEnd}
            currentPeriodEnd={subscription.currentPeriodEnd}
            onManageBilling={openPortal}
          />
        )}

        {!isLoading && plans.length > 0 && (
          <PlanCatalog
            plans={plans}
            currentPlanId={currentPlanId}
            busyPlanId={busyPlanId}
            onChoose={startCheckout}
          />
        )}

        {!isLoading && (
          <Box
            sx={{
              mt: 6,
              p: 3,
              borderRadius: 3,
              backgroundColor: colors.surface,
              border: `1px solid ${colors.border}`,
            }}
          >
            <Typography sx={overlineSx}>{t('billingHistory.overline')}</Typography>
            <Typography sx={{ color: colors.textMuted }}>{t('billingHistory.empty')}</Typography>
          </Box>
        )}
      </Box>
    </Box>
    </PageLayout>
  );
}

const overlineSx = {
  fontSize: '0.6875rem',
  fontWeight: 600,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: colors.textMuted,
  mb: 1.25,
} as const;

function CurrentPlanCard({
  plan,
  status,
  cancelAtPeriodEnd,
  currentPeriodEnd,
  onManageBilling,
}: {
  plan: PlanRow;
  status: string;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string | null;
  onManageBilling: () => void;
}) {
  const { t } = useTranslation('subscription');
  return (
    <Box
      sx={{
        mb: 4,
        p: 4,
        borderRadius: 3,
        backgroundColor: colors.surface,
        border: `1px solid ${colors.borderFocus}`,
        boxShadow: `0 0 0 1px ${colors.brandShadow}, 0 12px 40px rgba(106,255,69,0.06)`,
      }}
    >
      <Typography sx={overlineSx}>{t('currentPlan.overline')}</Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'flex-end' }} justifyContent="space-between" gap={2}>
        <Box>
          <Typography sx={{ fontSize: '2rem', fontWeight: 700, color: colors.text }}>
            {plan.name}
          </Typography>
          <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '1.25rem', color: colors.brand, mt: 0.5 }}>
            {priceLabel(plan)}
          </Typography>
          <Typography sx={{ fontSize: '0.8125rem', color: colors.textMuted, mt: 1 }}>
            {t(`status.${status}` as any, status)}
            {cancelAtPeriodEnd && currentPeriodEnd
              ? ` · ends ${new Date(currentPeriodEnd).toLocaleDateString()}`
              : ''}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          onClick={onManageBilling}
          sx={{
            borderColor: colors.border,
            color: colors.text,
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': { borderColor: colors.borderFocus, backgroundColor: 'transparent' },
          }}
        >
          {t('currentPlan.manageBilling')}
        </Button>
      </Stack>
      {plan.features?.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <ul style={{ margin: 0, paddingLeft: '1.25rem', color: colors.textMuted }}>
            {plan.features.map((f) => (
              <li key={f} style={{ marginBottom: 4 }}>
                <Typography component="span" sx={{ color: colors.textMuted, fontSize: '0.9rem' }}>{f}</Typography>
              </li>
            ))}
          </ul>
        </Box>
      )}
    </Box>
  );
}

function PlanCatalog({
  plans,
  currentPlanId,
  busyPlanId,
  onChoose,
}: {
  plans: PlanRow[];
  currentPlanId: string;
  busyPlanId: string | null;
  onChoose: (planId: string) => void;
}) {
  const { t } = useTranslation('subscription');
  return (
    <Box sx={{ mt: 2 }}>
      <Typography sx={overlineSx}>{t('switchPlan.overline')}</Typography>
      <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: colors.text, mb: 2.5 }}>
        {t('switchPlan.title')}
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
        {plans.map((p) => {
          const isCurrent = p.id === currentPlanId;
          return (
            <Box
              key={p.id}
              sx={{
                p: 3,
                borderRadius: 3,
                backgroundColor: colors.surface,
                border: `1px solid ${isCurrent ? colors.borderFocus : colors.border}`,
                transition: 'all 200ms',
                ...(isCurrent ? { boxShadow: `0 0 0 1px ${colors.brandShadow}` } : {}),
              }}
            >
              <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: colors.text, mb: 0.5 }}>
                {p.name}
              </Typography>
              <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '1.5rem', fontWeight: 700, color: colors.brand, mb: 0.25 }}>
                {priceLabel(p)}
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: colors.textMuted, mb: 2 }}>
                {p.priceCents === 0
                  ? t('switchPlan.free')
                  : p.interval === 'year'
                    ? '/ year'
                    : t('switchPlan.perMonth')}
              </Typography>
              {p.features?.length > 0 && (
                <ul style={{ margin: 0, paddingLeft: '1.1rem', color: colors.textMuted, minHeight: 110 }}>
                  {p.features.map((f) => (
                    <li key={f} style={{ marginBottom: 6 }}>
                      <Typography component="span" sx={{ color: colors.textMuted, fontSize: '0.85rem' }}>{f}</Typography>
                    </li>
                  ))}
                </ul>
              )}
              <Button
                fullWidth
                disabled={
                  isCurrent ||
                  busyPlanId === p.id ||
                  (!STRIPE_ENABLED && p.priceCents > 0)
                }
                onClick={() => onChoose(p.id)}
                sx={{
                  mt: 2,
                  py: 1.25,
                  backgroundColor: isCurrent ? colors.bgSubtle : colors.brand,
                  color: isCurrent ? colors.textMuted : colors.textInverse,
                  fontWeight: 700,
                  textTransform: 'none',
                  borderRadius: 2,
                  '&:hover': { backgroundColor: isCurrent ? colors.bgSubtle : colors.brandBright },
                  '&.Mui-disabled': { backgroundColor: colors.bgSubtle, color: colors.textMuted },
                }}
              >
                {isCurrent
                  ? t('switchPlan.current')
                  : busyPlanId === p.id
                    ? '…'
                    : !STRIPE_ENABLED && p.priceCents > 0
                      ? 'Coming soon'
                      : t('switchPlan.choose')}
              </Button>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function priceLabel(plan: PlanRow): string {
  if (plan.priceCents === 0) return '$0';
  const dollars = (plan.priceCents / 100).toFixed(2);
  const currency = (plan.currency || 'usd').toUpperCase();
  const symbol = currency === 'USD' ? '$' : `${currency} `;
  return `${symbol}${dollars}`;
}
